import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'

import { flexRender, type Row, type Table } from '@tanstack/react-table'
import type { VirtualItem, Virtualizer } from '@tanstack/react-virtual'

import { cn } from '@/lib/utils'

import AstaRightClickMenu from '../AstaRightClickMenu'
import { useContextMenu } from '../AstaRightClickMenu/useContextMenu'

interface TableCellProps {
	width: number | string
	children: React.ReactNode
}

const TableCell = memo(({ width, children }: TableCellProps) => (
	<td
		style={{
			width: `${width}px`,
			maxWidth: `${width}px`,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		}}
		className="px-2 py-2"
	>
		{children}
	</td>
))
TableCell.displayName = 'TableCell'

type RowContainerProps = {
	isClickable: boolean
	isSelected: boolean
	row: VirtualItem
	children: React.ReactNode
	onContextMenu: (e: React.MouseEvent) => void
	onClick: () => void
	onKeyDown: (e: React.KeyboardEvent) => void
	measureElement: (element: HTMLElement | null) => void
	dataIndex: number
	isLastRow: boolean
}

const RowContainer = memo(
	({
		isClickable,
		isSelected,
		row,
		children,
		onContextMenu,
		onClick,
		onKeyDown,
		measureElement,
		dataIndex,
		isLastRow,
	}: RowContainerProps) => (
		<tr
			data-index={dataIndex}
			ref={measureElement}
			onContextMenu={onContextMenu}
			onClick={onClick}
			onKeyDown={onKeyDown}
			tabIndex={isClickable ? 0 : -1}
			aria-selected={isSelected}
			style={{
				position: 'absolute',
				top: 0,
				left: 0,
				width: '100%',
				transform: `translateY(${row.start}px)`,
			}}
			className={cn(
				'text-foreground bg-background select-none font-sans text-sm',
				!isLastRow && 'border-b',
				isClickable && 'cursor-pointer',
				!isClickable && 'cursor-default',
				isSelected && 'bg-accent',
				!isSelected && 'hover:bg-accent/50 dark:hover:bg-accent/10'
			)}
		>
			{children}
		</tr>
	)
)
RowContainer.displayName = 'RowContainer'

type ContextMenuOption = {
	label: string
	onClick: (rows: unknown[]) => void
}

interface TableBodyProps<T> {
	table: Table<T>
	rows: VirtualItem[]
	height: number
	selectedRowId?: string | number
	rowOnClick?: (data: T) => void
	setRowSelection?: (value: unknown) => void
	contextMenuOptions?: ContextMenuOption[]
	rowVirtualizer: Virtualizer<HTMLDivElement, Element>
}

interface VirtualRowProps<T> {
	row: VirtualItem
	tableRow: Row<T>
	isSelected: boolean
	isClickable: boolean
	handleRowClick: (row: Row<T>) => void
	handleContextMenu: (row: Row<T>) => void
	setClicked: (clicked: boolean) => void
	setCoords: (coords: { x: number; y: number }) => void
	setClickedRow: (row: VirtualItem) => void
	contextMenuOptions: ContextMenuOption[]
	measureElement: (element: HTMLElement | null) => void
	columnSizingVersion: number
	isLastRow: boolean
}

// Create a memoized row component to prevent unnecessary re-renders
function VirtualRowComponent<T>(props: VirtualRowProps<T>) {
	const {
		row,
		tableRow,
		isSelected,
		isClickable,
		handleRowClick,
		handleContextMenu,
		setClicked,
		setCoords,
		setClickedRow,
		contextMenuOptions,
		measureElement,
		isLastRow,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		columnSizingVersion, // Used only for dependency tracking in memo
	} = props

	// Important: Use getVisibleCells() to get cells in the correct order
	// based on the current column order and visibility
	const visibleCells = tableRow.getVisibleCells()

	const handleContextMenuEvent = useCallback(
		(e: React.MouseEvent) => {
			if (contextMenuOptions.length === 0) return
			e.preventDefault()
			setClicked(true)
			setCoords({ x: e.pageX, y: e.pageY })
			setClickedRow(row)
			handleContextMenu(tableRow)
		},
		[
			contextMenuOptions.length,
			setClicked,
			setCoords,
			setClickedRow,
			row,
			handleContextMenu,
			tableRow,
		]
	)

	const handleClick = useCallback(() => {
		handleRowClick(tableRow)
	}, [handleRowClick, tableRow])

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' || e.key === ' ') {
				handleRowClick(tableRow)
			}
		},
		[handleRowClick, tableRow]
	)

	return (
		<RowContainer
			dataIndex={row.index}
			measureElement={measureElement}
			onContextMenu={handleContextMenuEvent}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			isClickable={isClickable}
			isSelected={isSelected}
			row={row}
			isLastRow={isLastRow}
		>
			{visibleCells.map(cell => {
				// Get the exact size from the column to ensure it matches the header
				const size = cell.column.getSize()
				// Only use default if size is not available
				const width = isNaN(size) ? 150 : size
				return (
					<TableCell key={cell.id} width={width}>
						{flexRender(
							cell.column.columnDef.cell,
							cell.getContext()
						)}
					</TableCell>
				)
			})}
		</RowContainer>
	)
}

// Memoize the row component
const VirtualRow = memo(VirtualRowComponent) as <T>(
	props: VirtualRowProps<T>
) => JSX.Element

const NO_OPTIONS: ContextMenuOption[] = []

export function TableBody<T>({
	table,
	rows,
	height,
	rowOnClick,
	setRowSelection,
	contextMenuOptions = NO_OPTIONS,
	rowVirtualizer,
	selectedRowId,
}: TableBodyProps<T>): JSX.Element {
	const { clicked, setClicked, coords, setCoords } = useContextMenu()
	const [clickedRow, setClickedRow] = useState<VirtualItem>(null)
	const tableRows = table.getRowModel().rows
	const totalRowCount = tableRows.length
	const [columnSizingVersion, setColumnSizingVersion] = useState(0)

	const prevColumnSizingRef = useRef<any>(null)
	const prevColumnOrderRef = useRef<string[]>(null)
	const prevColumnVisibilityRef = useRef<Record<string, boolean>>(null)

	const handleContextMenu = useCallback(
		(row: Row<T>) => {
			if (!row.getIsSelected()) {
				setRowSelection?.({})
			}
		},
		[setRowSelection]
	)

	const handleRowClick = useCallback(
		(row: Row<T>) => {
			if (rowOnClick) {
				rowOnClick(row.original)
			}
		},
		[rowOnClick]
	)

	// Memoize context menu options to prevent recreating on each render
	const memoizedContextMenuOptions = useMemo(() => {
		if (!clicked || contextMenuOptions.length === 0 || !clickedRow)
			return null

		return (
			<AstaRightClickMenu
				left={coords.x}
				transform={`translateY(${clickedRow.start + 30}px)`}
				contextMenuOptions={contextMenuOptions.map(op => ({
					...op,
					label: op.label,
					onClick: () => {
						if (!tableRows[clickedRow.index].getIsSelected()) {
							op.onClick([tableRows[clickedRow.index].original])
							return
						}
						op.onClick(
							table
								.getSelectedRowModel()
								?.rows?.map(row => row.original)
						)
						setRowSelection?.({})
					},
				}))}
			/>
		)
	}, [
		clicked,
		coords,
		clickedRow,
		contextMenuOptions,
		tableRows,
		table,
		setRowSelection,
	])

	const rowSelection = table.getState().rowSelection

	// Pre-calculate row selection status
	const selectedStates = useMemo(() => {
		return rows.map(row => {
			return {
				isSelected:
					rowSelection[row.key] ||
					(selectedRowId != null &&
						(
							tableRows[row.index].original as unknown as {
								id: string
							}
						)?.id === selectedRowId),
			}
		})
	}, [rows, rowSelection, selectedRowId, tableRows])

	const isAnyColumnResizing = table
		.getAllLeafColumns()
		.some(col => col.getIsResizing())

	// Detect column sizing, order, and visibility changes and force rerender of all rows
	useEffect(() => {
		const columnSizing = table.getState().columnSizing
		const columnOrder = table.getState().columnOrder
		const columnVisibility = table.getState().columnVisibility

		const hasSizingChanged =
			JSON.stringify(columnSizing) !==
			JSON.stringify(prevColumnSizingRef.current)
		const hasOrderChanged =
			JSON.stringify(columnOrder) !==
			JSON.stringify(prevColumnOrderRef.current)
		const hasVisibilityChanged =
			JSON.stringify(columnVisibility) !==
			JSON.stringify(prevColumnVisibilityRef.current)

		if (hasSizingChanged || hasOrderChanged || hasVisibilityChanged) {
			setColumnSizingVersion(prev => prev + 1)

			if (!isAnyColumnResizing) {
				rowVirtualizer.measure()
			}

			prevColumnSizingRef.current = columnSizing
			prevColumnOrderRef.current = columnOrder
			prevColumnVisibilityRef.current = columnVisibility
		}
	}, [
		table.getState().columnSizing,
		table.getState().columnOrder,
		table.getState().columnVisibility,
		rowVirtualizer,
		isAnyColumnResizing,
	])

	return (
		<tbody
			style={{ position: 'relative', height: `${height}px` }}
			role="table"
			aria-rowcount={rows.length}
		>
			{rows.map((virtualRow, index) => {
				const tableRow = tableRows[virtualRow.index]
				const { isSelected } = selectedStates[index]
				const isLastRow = virtualRow.index === totalRowCount - 1

				return (
					<VirtualRow<T>
						key={`${virtualRow.key}-${columnSizingVersion}`}
						row={virtualRow}
						tableRow={tableRow}
						isSelected={isSelected}
						isClickable={Boolean(rowOnClick)}
						handleRowClick={handleRowClick}
						handleContextMenu={handleContextMenu}
						setClicked={setClicked}
						setCoords={setCoords}
						setClickedRow={setClickedRow}
						contextMenuOptions={contextMenuOptions}
						measureElement={rowVirtualizer.measureElement}
						columnSizingVersion={columnSizingVersion}
						isLastRow={isLastRow}
					/>
				)
			})}
			{memoizedContextMenuOptions}
		</tbody>
	)
}
